import React, { useMemo } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { PREMIUM_FEATURES } from "../../common/flatRateBilling";
import { ActionsPage } from "../actions/ActionsPage";
import { ActionsProvider } from "../actions/actionsContext";
import { ActionsTabs } from "../actions/ActionsTabs";
import { AccountingPage } from "../accounting/AccountingPage";
import { BalanceSheetAccessPage } from "../accounting/balanceSheet/BalanceSheetAccessPage";
import { ChartOfAccountsPage } from "../accounting/chartOfAccounts/ChartOfAccounts";
import { GeneralLedgerAccessPage } from "../accounting/generalLedger/GeneralLedgerAccessPage";
import { ProfitLossPreviewPage } from "../accounting/profitLoss/ProfitLossPreviewPage";
import { AccountingTabProvider } from "../accounting/useAccountingTab";
import { TrialBalanceAccessPage } from "../accounting/trialBalance/TrialBalanceAccessPage";
import { AccountsPage } from "../accounts/AccountsPage";
import { AddAccountsRoute } from "../accounts/AddAccounts/AddAccountsRoute";
import { Convert } from "../billing/Convert";
import { Subscribe } from "../billing/Subscribe";
import { Upgrade } from "../billing/Upgrade";
import { DocsToDocumentsRedirect } from "../documents/DocsToDocumentsRedirect";
import { FinancialDocumentsPage } from "../documents/FinancialDocumentsPage";
import { FinancialDocumentUploadProvider } from "../documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { FeatureAccess } from "../general/FeatureAccess/FeatureAccess";
import { InvoicesPage } from "../invoices/InvoicesPage";
import { NewEntity } from "../onboarding/flows/NewEntity";
import { OAuthAuthorize } from "../oauth/authorization/OAuthAuthorize";
import { OAuthCallback } from "../oauth/authorization/OAuthCallback";
import { CashFlowPage } from "../reports/CashFlowPage";
import { CategoriesPage } from "../settings/Categories/CategoriesPage";
import { ClassesPage } from "../settings/Classes/ClassesPage";
import { RulesPage } from "../settings/Rules/RulesPage";
import { SettingsRoute } from "../settings/SettingsRoute";
import { SimulationsDashboard } from "../simulations/SimulationsDashboard";
import { TransactionsPageContainer } from "../transactions/TransactionsPageContainer";
import { MainInterface, MainInterfaceProps } from "./MainInterface";

export const MainRoutes: React.FC = () => {
    const location = useLocation();
    const accountingBreadcrumbs = useMemo(() => {
        const breadcrumbs: MainInterfaceProps["breadcrumbs"] = [
            {
                label: "Accounting",
                to: "/accounting",
            },
        ];

        switch (location.pathname) {
            case "/accounting/profit-loss":
                breadcrumbs.push({
                    label: "Profit & Loss",
                });
                break;
            case "/accounting/balance-sheet":
                breadcrumbs.push({
                    label: "Balance Sheet",
                });
                break;
            case "/accounting/general-ledger":
                breadcrumbs.push({
                    label: "General Ledger",
                });
                break;
            case "/accounting/chart-of-accounts":
                breadcrumbs.push({
                    label: "Chart of Accounts",
                });
                break;
            case "/accounting/trial-balance":
                breadcrumbs.push({
                    label: "Trial Balance",
                });
                break;
            default:
                delete breadcrumbs[0].to; // not interactable when there are no breadcrumbs
                break;
        }

        return breadcrumbs;
    }, [location.pathname]);

    return (
        <Routes>
            <Route
                path="/"
                element={
                    <ActionsProvider>
                        <MainInterface
                            breadcrumbs={[{ label: "Actions" }]}
                            subNavigation={<ActionsTabs />}
                        >
                            <ActionsPage />
                        </MainInterface>
                    </ActionsProvider>
                }
            />

            <Route
                path="/actions/:category?"
                element={
                    <ActionsProvider>
                        <MainInterface
                            breadcrumbs={[{ label: "Actions" }]}
                            subNavigation={<ActionsTabs />}
                        >
                            <ActionsPage />
                        </MainInterface>
                    </ActionsProvider>
                }
            />

            <Route
                path="/invoicing"
                element={
                    <MainInterface breadcrumbs={[{ label: "Invoicing" }]}>
                        <InvoicesPage />
                    </MainInterface>
                }
            />

            <Route
                path="/accounts"
                element={
                    <MainInterface breadcrumbs={[{ label: "Accounts" }]}>
                        <AccountsPage />
                    </MainInterface>
                }
            />

            <Route
                path="/transactions/:entityId?"
                element={
                    <MainInterface breadcrumbs={[{ label: "Transactions" }]}>
                        <TransactionsPageContainer />
                    </MainInterface>
                }
            />

            <Route
                path="/rules"
                element={
                    <MainInterface breadcrumbs={[{ label: "Rules" }]}>
                        <RulesPage />
                    </MainInterface>
                }
            />

            <Route
                path="/categories"
                element={
                    <MainInterface breadcrumbs={[{ label: "Categories" }]}>
                        <CategoriesPage />
                    </MainInterface>
                }
            />

            <Route
                path="/classes"
                element={
                    <MainInterface breadcrumbs={[{ label: "Classes" }]}>
                        <ClassesPage />
                    </MainInterface>
                }
            />

            <Route
                path="/cashflow"
                element={
                    <MainInterface breadcrumbs={[{ label: "Cash Flow" }]}>
                        <CashFlowPage />
                    </MainInterface>
                }
            />

            <Route
                path="/documents/:documentId?/:view?"
                element={
                    <MainInterface breadcrumbs={[{ label: "Documents" }]}>
                        <FinancialDocumentUploadProvider>
                            <FinancialDocumentsPage />
                        </FinancialDocumentUploadProvider>
                    </MainInterface>
                }
            />

            <Route path="/docs/*" element={<DocsToDocumentsRedirect />} />

            <Route
                path="/accounting"
                element={
                    <MainInterface breadcrumbs={accountingBreadcrumbs}>
                        <AccountingTabProvider>
                            <Outlet />
                        </AccountingTabProvider>
                    </MainInterface>
                }
            >
                <Route index element={<AccountingPage />} />
                <Route path="profit-loss" element={<ProfitLossPreviewPage />} />
                <Route
                    path="balance-sheet"
                    element={<BalanceSheetAccessPage />}
                />
                <Route
                    path="general-ledger"
                    element={<GeneralLedgerAccessPage />}
                />
                <Route
                    path="chart-of-accounts"
                    element={<ChartOfAccountsPage />}
                />
                <Route
                    path="trial-balance"
                    element={<TrialBalanceAccessPage />}
                />
            </Route>

            <Route
                path="/new-entity"
                element={
                    <FeatureAccess
                        feature={PREMIUM_FEATURES.UNLIMITED_ENTITIES}
                        renderAllowed={() => <NewEntity />}
                        renderBlocked={() => (
                            <Navigate to="/settings/plans" replace />
                        )}
                    />
                }
            />

            <Route path="/settings/*" element={<SettingsRoute />} />
            <Route path={`/add-account`} element={<AddAccountsRoute />} />
            <Route path="/simulations" element={<SimulationsDashboard />} />

            <Route path="/oauth">
                <Route path="authorize/:service" element={<OAuthAuthorize />} />
                <Route path="callback/:service" element={<OAuthCallback />} />
            </Route>

            <Route path="/subscribe/:plan" element={<Subscribe />} />

            <Route path="/upgrade/:plan" element={<Upgrade />} />

            <Route path="/convert" element={<Convert />} />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};
